import { defineStore } from "pinia";
import { IUserStatusDetails } from "../../../resources-vue/vue-interfaces/messaging-service/i-user";

import { isEmptyGuid } from "../../../common/utilities/empty-guid";
import { Container } from "aurelia-dependency-injection";
import { MessageService } from "../../../services/message-service";

export const useUserStore = defineStore("users", {
    state() {
        return {
            users: {} as Record<string, IUserStatusDetails>,

            userIdToLoginId: {} as Record<string, string>,
            loginIdToUserId: {} as Record<string, string>,

            cache: {}
        };
    },

    getters: {
        getUser: (state) => {
            return (loginId: string) => {
                return state.users[loginId];
            };
        },

        getMultipleUsers: (state) => {
            return (loginIds: string[]) => {
                return loginIds.flatMap((id: string) => {
                    // handle user details not found
                    if (state.users[id]) {
                        return state.users[id];
                    }
                    return [];
                });
            };
        },

        getLoginIdByUserId: (state) => {
            return (userId: string) => {
                return state.userIdToLoginId[userId];
            };
        },

        getUserIdByLoginId: (state) => {
            return (loginId: string) => {
                return state.loginIdToUserId[loginId];
            };
        },

        getUserStatusDetails: (state) => {
            return (loginId: string) => {
                return state.users[loginId] ?? {};
            };
        },
        getUserStatusMsg: (state) => {
            return (loginId: string) => {
                if (state.users[loginId]) {
                    return state.users[loginId].statusMessage;
                }
                return "";
            };
        },
        getDisplayStatusMsg() {
            return (loginIds: any[]) => {
                let msg = null;
                const users = this.getMultipleUsers(loginIds);
                const usersWithStatus = users?.filter(
                    (user: any) => this.users[user.loginId] && this.users[user.loginId].statusMessage.trim() !== ""
                );
                if (usersWithStatus) {
                    if (usersWithStatus.length === 0) {
                        msg = null;
                    } else if (usersWithStatus.length === 1) {
                        const status = this.users[usersWithStatus[0].loginId].statusMessage;
                        msg = `Status of ${usersWithStatus[0].firstName} ${usersWithStatus[0].lastName}: ${status}`;
                    } else if (usersWithStatus.length === 2) {
                        msg = `${usersWithStatus[0].firstName} ${usersWithStatus[0].lastName} and ${usersWithStatus[1].firstName} ${usersWithStatus[1].lastName} have a status message.`;
                    } else msg = "3 or more users have a status message.";
                }
                return msg;
            };
        }
    },

    actions: {
        upsertUsers(users: any[]) {
            users.forEach((user: any) => {
                this.users[user.loginId] = {
                    firstName: user.firstName,
                    lastName: user.lastName,
                    userId: user.userId,
                    loginId: user.loginId,
                    email: user.email,
                    currentStatus: user.currentStatus,
                    profileColor: user.profileColor,
                    profilePicId: user.profilePicId,
                    statusMessage: user.statusMessage
                };

                if (user.userId && user.loginId) {
                    this.userIdToLoginId[user.userId] = user.loginId;
                    this.loginIdToUserId[user.loginId] = user.userId;
                }
            });
        },

        async fetchImage(loginId: string, imageId: string) {
            // If image is removed -> clear cache
            if (isEmptyGuid(imageId)) {
                this._removeUser(loginId);
                return null;
            }

            const cached = this.cache[loginId];

            // If imageId did not change -> reuse cached image
            if (cached?.imageId === imageId) {
                return cached.imageUrl;
            }

            const messageService = Container.instance.get(MessageService);
            const asset = await messageService.getAssetFromId({ id: imageId });
            // Store new image
            this.cache[loginId] = {
                imageId,
                imageUrl: asset.url
            };

            return asset.url;
        },

        async onImageUpdated(loginId: string, newImageId: string) {
            // Empty GUID -> user removed image
            if (isEmptyGuid(newImageId)) {
                this._removeUser(loginId);
                return;
            }
            await this.fetchImage(loginId, newImageId);
        },

        _removeUser(loginId: string) {
            delete this.cache[loginId];
        },

        setUserStatus(loginId: string, data: any) {
            this.users[loginId] = {
                ...this.users[loginId],
                ...data
            };
        },

        setStatusManually(loginId: string, data: any) {
            const [key, value] = data;
            this.users[loginId][key] = value;
        }
    }
});
